import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import HarbourIcon from './HarbourIcon';
import HarbourIconButton from './HarbourIconButton';
import HarbourButton from './HarbourButton';
import HarbourPathwayMark from './HarbourPathwayMark';
import './harbour.css';

/*
 * "Before you start" (TDI-53 section 04): orientation before the first
 * question of a school form — how long it takes, answers autosave, it can be
 * done in stages, and nobody gets stuck. Shown once per form until started.
 */
const SHARED_TIPS = [
  { icon: 'help', title: 'Not sure?', body: 'Many questions have an "I don’t know" answer you can choose.' },
  { icon: 'clock', title: 'Take breaks', body: 'Your answers save as you go, you can come back and finish later.' },
  { icon: 'edit', title: 'Change answers later', body: 'Change any answer on the review page, right up until you confirm and submit. After that they are locked.' },
];
const TEACHER_CHANGE_TIP = {
  icon: 'edit',
  title: 'Change answers later',
  body: 'Change any answer on the review page until your SENCO submits the school’s forms. After that they are locked.',
};
const ROLE_TIP = {
  school_user: { icon: 'eye', title: 'Answer from school', body: 'The parent or carer answers for home. Neither side sees the other’s answers.' },
  guardian: { icon: 'eye', title: 'Answer from home', body: 'The school answers for school. Neither side sees the other’s answers.' },
};

export default function HarbourBeforeYouStart({
  open,
  onStart,
  formTitle,
  tone = 'sen',
  questionCount,
  minutes,
  childFirstName,
  role = 'school_user',
  // Whether the form really offers an "I don't know" answer. The tip promises
  // one, so it is left out when the form has none (honesty: never imply more
  // than the form delivers).
  offersDontKnow = true,
  // A class teacher (TDI-618, IN-13): their own submit locks nothing; only
  // the SENCO's confirm does, so the change-answers tip says so.
  isTeacher = false,
}) {
  const tips = [
    ...SHARED_TIPS
      .filter((tip) => offersDontKnow || tip.title !== 'Not sure?')
      .map((tip) => (isTeacher && tip.title === 'Change answers later' ? TEACHER_CHANGE_TIP : tip)),
    ROLE_TIP[role] || ROLE_TIP.school_user,
  ];
  const contextBits = [
    questionCount ? `${questionCount} questions` : null,
    minutes ? `about ${minutes} minutes` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next) onStart(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="harbour harbour-scrim" />
        <Dialog.Content
          className="harbour harbour-card fixed left-1/2 top-1/2 z-50 w-[min(800px,calc(100vw-32px))] max-h-[calc(100vh-32px)] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-3xl p-8 md:p-10 flex flex-col gap-6"
          aria-describedby={undefined}
        >
          <div className="flex items-start gap-4">
            <div className="flex flex-col gap-1 min-w-0">
              {/* TDI-509: the form name has the row to itself. Sharing it
                  with the question count left the name wrapping one word per
                  line on a phone ("Thinking / and / organising"), on the
                  screen every parent sees before every form. */}
              <span className="flex items-center gap-2 harbour-body-16">
                <HarbourPathwayMark pathway={tone} />
                <span className="harbour-body-16-bold">{formTitle}</span>
              </span>
              {contextBits && (
                <span className="harbour-body-16 text-[color:var(--harbour-colour-text-muted)]">
                  {contextBits}
                </span>
              )}
              <Dialog.Title asChild>
                <h1 className="harbour-heading-32">Before you start</h1>
              </Dialog.Title>
            </div>
            <span className="ml-auto flex-none">
              <HarbourIconButton kind="ghost" icon="close" label="Close and start the questions" onClick={onStart} />
            </span>
          </div>

          {childFirstName && (
            <div className="harbour-notice harbour-notice--info items-center">
              <span className="harbour-notice__icon"><HarbourIcon name="user" size={20} /></span>
              <div className="flex-1 min-w-0">
                <p className="harbour-notice__title">No other children to compare with?</p>
                <p className="harbour-body-16">
                  {`That’s fine. Answer from what is usual at ${childFirstName}’s age, in your experience.`}
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tips.map((tip) => (
              <div key={tip.title} className="rounded-2xl bg-[color:var(--harbour-lane-not-active-bg)] p-5 flex flex-col gap-3">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[color:var(--harbour-colour-brand-plum-10)] text-[color:var(--harbour-colour-brand-plum)]">
                  <HarbourIcon name={tip.icon} size={20} />
                </span>
                <div>
                  <p className="harbour-body-16-bold">{tip.title}</p>
                  <p className="harbour-body-16">{tip.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <HarbourButton kind={role === 'guardian' ? 'family' : 'primary'} trailingIcon="arrow-right" onClick={onStart}>
              Start questions
            </HarbourButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
